/**
 * ROI (Release of Information) utility helpers.
 *
 * Responsibilities:
 * 1. Derive the display status for a contact's ROI (active / expired / pending / none).
 * 2. Map a FHIR Consent resource → ContactRoi view-model.
 * 3. Match a Consent to a Contact (RelatedPerson or Patient.contact).
 * 4. Merge Consents into a Contact[] array.
 * 5. Build FHIR Consent resources for new ROIs.
 *
 * Status derivation rules (applied in order):
 *  1. No roi field                → 'none'
 *  2. consentStatus === 'inactive' → 'none' (revoked; not shown in UI)
 *  3. consentStatus === 'draft'    → 'pending'
 *  4. expirationDate is in the past (date-only comparison) → 'expired'
 *  5. otherwise                   → 'active'
 *
 * These rules are deterministic and testable with no side effects.
 *
 * IMPORTANT: ROI authorization is distinct from portal/app access.
 *   - ROI  → a patient signed a Release of Information allowing a contact to receive PHI.
 *   - Portal access → a contact has a login account for the client portal.
 * Never conflate these two concepts in labels, tooltips, or documentation.
 */

import type { Consent } from '@medplum/fhirtypes';
import {
  ROI_CONSENT_PURPOSE_SYSTEM,
  ROI_CONSENT_OTHER_DESCRIPTION_EXT,
  ROI_PATIENT_CONTACT_NAME_EXT,
  ROI_CONSENT_CATEGORY_CODE,
  CONSENT_CATEGORY_SYSTEM,
} from '@unify/fhir-model/constants';
import {
  buildRoiConsent,
  type BuildRoiConsentParams,
  type RoiPurposeCode,
} from '@unify/fhir-model/resources/consent';
import type { Contact, ContactRoi, RoiPermissionKey } from '../data/contactTypes';

// ─── Status ───────────────────────────────────────────────────────────

export type RoiStatus = 'active' | 'expired' | 'pending' | 'none';

/** Consent statuses excluded from contact ROI display (withdrawn or revoked). */
function isWithdrawnRoiConsentStatus(status: Consent['status'] | undefined): boolean {
  return status === 'rejected' || status === 'inactive';
}

/**
 * Derives the display status for a contact's ROI authorization.
 * Date-only comparison (no time) so local timezone does not affect expiry display.
 */
export function resolveRoiStatus(roi: ContactRoi | undefined): RoiStatus {
  if (!roi) return 'none';
  if (roi.consentStatus === 'inactive') return 'none';
  if (roi.consentStatus === 'draft') return 'pending';
  if (roi.expirationDate) {
    const exp = new Date(roi.expirationDate);
    const today = new Date();
    // Compare dates only — strip time component
    exp.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    if (exp < today) return 'expired';
  }
  return 'active';
}

// ─── FHIR Consent → ContactRoi mapping ───────────────────────────────

/** Map a FHIR Consent to the ContactRoi view-model. */
export function mapConsentToContactRoi(consent: Consent): ContactRoi {
  const period = consent.provision?.period;

  const permissions: Partial<Record<RoiPermissionKey, boolean>> = {};
  // ConsentProvision.purpose is Coding[] (flat), not CodeableConcept[]
  for (const coding of consent.provision?.purpose ?? []) {
    if (coding.system === ROI_CONSENT_PURPOSE_SYSTEM && coding.code) {
      permissions[coding.code as RoiPermissionKey] = true;
    }
  }

  const otherDescription = consent.extension?.find(
    (e) => e.url === ROI_CONSENT_OTHER_DESCRIPTION_EXT,
  )?.valueString;

  return {
    dateSigned: period?.start ?? consent.dateTime?.slice(0, 10) ?? '',
    expirationDate: period?.end,
    permissions,
    otherDescription,
    consentId: consent.id,
    consentStatus: (consent.status as ContactRoi['consentStatus']) ?? 'active',
  };
}

// ─── Contact–Consent matching ─────────────────────────────────────────

/**
 * Returns true when the Consent authorizes the given contact.
 *
 * - RelatedPerson contacts: match by `provision.actor[].reference.reference`
 * - Patient.contact entries: match by the `roi-patient-contact-name` extension value
 */
export function matchConsentToContact(consent: Consent, contact: Contact): boolean {
  if (contact.source === 'related-person' && contact.relatedPersonId) {
    return (consent.provision?.actor ?? []).some(
      (a) => a.reference?.reference === `RelatedPerson/${contact.relatedPersonId}`,
    );
  }
  // Patient.contact entries (no RelatedPerson id) — match by display name extension
  const extName = consent.extension?.find((e) => e.url === ROI_PATIENT_CONTACT_NAME_EXT)?.valueString;
  if (extName) {
    return extName === `${contact.legalFirstName} ${contact.legalLastName}`;
  }
  return false;
}

/**
 * For a set of contacts, finds the most relevant Consent for each one and
 * populates `contact.roi`. Active Consent takes priority over inactive/draft
 * when multiple Consents exist for the same contact.
 *
 * Only processes ROI Consents (category = roi).
 */
export function resolveContactsWithRoi(contacts: Contact[], consents: Consent[]): Contact[] {
  const roiConsents = consents.filter((c) =>
    (c.category ?? []).some((cc) =>
      (cc.coding ?? []).some(
        (cod) => cod.system === CONSENT_CATEGORY_SYSTEM && cod.code === ROI_CONSENT_CATEGORY_CODE,
      ),
    ),
  );

  return contacts.map((contact) => {
    const matches = roiConsents.filter(
      (c) => !isWithdrawnRoiConsentStatus(c.status) && matchConsentToContact(c, contact),
    );
    if (matches.length === 0) return contact;

    // Pick active first, then most recent by dateTime
    const activeFirst = [...matches].sort((a, b) => {
      if (a.status === 'active' && b.status !== 'active') return -1;
      if (b.status === 'active' && a.status !== 'active') return 1;
      return (b.dateTime ?? '').localeCompare(a.dateTime ?? '');
    });

    return { ...contact, roi: mapConsentToContactRoi(activeFirst[0]) };
  });
}

// ─── Consent builder (wraps fhir-model) ──────────────────────────────

export { buildRoiConsent };
export type { BuildRoiConsentParams, RoiPurposeCode };
